import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { LogOut, LayoutDashboard, ChevronRight, ChevronDown, ChevronUp, CreditCard, Sparkles, Loader2, KeyRound, ShieldCheck, Rocket } from "lucide-react";
import { toast } from "sonner";

// Slim progress bar used inside the trial summary.
function TrialBar({ label, right, pct }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-slate-400 mb-1">
        <span>{label}</span><span>{right}</span>
      </div>
      <div className="h-1.5 rounded-full bg-[#0E131F]/70 overflow-hidden">
        <div className="h-full bg-[#00E5FF] transition-all" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
    </div>
  );
}

// Settings body, shared by the mobile /settings page and the desktop slide-over
// drawer. `onClose` is called before any navigation so the drawer closes cleanly.
export default function SettingsContent({ onClose = () => {} }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [translation, setTranslation] = useState("niv");
  const [studyMode, setStudyMode] = useState("companion");
  const [unreplied, setUnreplied] = useState(0);
  const [sub, setSub] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [topupBusy, setTopupBusy] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [updatesOpen, setUpdatesOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: s }, { data: sb }, { data: up }] = await Promise.all([
          api.get("/config"),
          api.get("/settings"),
          api.get("/me/subscription"),
          api.get("/updates"),
        ]);
        setConfig(cfg);
        setTranslation(s.translation || "niv");
        setStudyMode(s.study_mode || "companion");
        setSub(sb);
        setUpdates(up.updates || []);
      } catch {
        toast.error("Couldn't load some settings — please try again.");
      }
      if (user?.role === "admin") {
        try {
          const { data } = await api.get("/admin/stats");
          setUnreplied(data.unreplied_feedback || 0);
        } catch {
          /* non-fatal */
        }
      }
    })();
  }, [user]);

  const buyTopup = async (lookup_key) => {
    setTopupBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setTopupBusy("");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: curPw, new_password: newPw });
      toast.success("Your password has been updated");
      setCurPw("");
      setNewPw("");
      setPwOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't change password");
    }
    setPwBusy(false);
  };

  const savePrefs = async (patch) => {
    try {
      await api.put("/settings", patch);
      toast.success("Preference saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const doLogout = async () => {
    await logout();
    onClose();
    navigate("/login");
  };

  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-3xl text-white">Settings</h1>
        <p className="text-sm text-slate-400 mt-2">{user?.email}</p>
      </header>

      {sub && (
        <section className="space-y-3" data-testid="settings-subscription">
          <button
            data-testid="manage-subscription-toggle"
            onClick={() => setSubOpen((o) => !o)}
            className="w-full flex items-center justify-between bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 hover:border-[#00E5FF]/30 transition-colors"
          >
            <span className="flex items-center gap-3 text-slate-300">
              <CreditCard className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
              <span className="text-left">
                <span className="block font-medium">Manage subscription</span>
                <span className="block text-xs text-slate-500">
                  {sub.plan?.name}
                  {sub.tier === "free" && sub.trial?.active
                    ? ` · trial · ${sub.trial.days_left} ${sub.trial.days_left === 1 ? "day" : "days"} left`
                    : ""}
                </span>
              </span>
            </span>
            {subOpen ? <ChevronUp className="w-5 h-5 text-slate-500" strokeWidth={1.5} /> : <ChevronDown className="w-5 h-5 text-slate-500" strokeWidth={1.5} />}
          </button>

          {subOpen && (
            <div className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-5 space-y-4" data-testid="subscription-panel">
              <div className="flex items-baseline justify-between">
                <span className="text-2xl text-white">{sub.plan?.name}</span>
                <span className="text-sm text-slate-400">{sub.plan?.price ? `$${sub.plan.price}/mo` : "Free"}</span>
              </div>
              <div className="flex gap-6 text-sm text-slate-400">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Credits</div>
                  <div className="text-lg text-slate-200" data-testid="settings-credits">{sub.uncapped ? "Unlimited" : (sub.credits_total ?? sub.credits)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Go Deeper</div>
                  <div className="text-lg text-slate-200">
                    {sub.go_deeper?.unlimited ? "Unlimited" : `${sub.go_deeper?.remaining} left this month`}
                  </div>
                </div>
              </div>
              {sub.grandfathered && (
                <p className="text-xs text-[#00E5FF]">Thank you for being an early friend of OpenWord AI — Go Deeper stays unlimited for you.</p>
              )}
              {sub.tier === "free" && sub.trial?.active && (
                <div className="space-y-3 pt-1" data-testid="settings-trial">
                  <div className="flex items-center justify-between text-xs">
                    <span className="uppercase tracking-widest text-[#00E5FF] font-medium">Free trial</span>
                    <span className="text-slate-400">{sub.trial.days_left} of {sub.trial.total_days} days left</span>
                  </div>
                  <TrialBar label="Days" right={`${sub.trial.days_left} left`} pct={(((sub.trial.total_days || 7) - sub.trial.days_left) / (sub.trial.total_days || 7)) * 100} />
                  <TrialBar label="Credits" right={`${sub.credits_total ?? sub.credits} of 100 left`} pct={((100 - (sub.credits_total ?? sub.credits)) / 100) * 100} />
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Full access to the creation tools, no card needed. When your trial ends you&rsquo;ll stay on the free plan &mdash; you keep everything you&rsquo;ve written and can keep reading and studying. To keep creating new sermons and prayer studies, you can upgrade to a paid plan anytime. We never charge you automatically.
                  </p>
                </div>
              )}
              <div className="space-y-2 pt-1" data-testid="settings-topups">
                <div className="text-xs font-medium uppercase tracking-widest text-[#00E5FF]">Need more credits?</div>
                <p className="text-xs text-slate-400">One-time top-ups that never expire.</p>
                <div className="grid grid-cols-3 gap-2">
                  {(sub.topup_packs || []).map((pack) => (
                    <button
                      key={pack.lookup_key}
                      data-testid={`settings-topup-${pack.lookup_key}`}
                      onClick={() => buyTopup(pack.lookup_key)}
                      disabled={!!topupBusy}
                      className="rounded-xl border border-[#00E5FF]/20 bg-[#0E131F] p-3 flex flex-col items-center gap-1 hover:border-[#00E5FF]/50 transition-colors disabled:opacity-60"
                    >
                      {topupBusy === pack.lookup_key ? (
                        <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" />
                      ) : (
                        <Sparkles className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} />
                      )}
                      <span className="text-lg text-white leading-none">{pack.credits}</span>
                      <span className="text-[11px] text-slate-500">credits</span>
                      <span className="text-xs font-medium text-[#00E5FF]">${pack.price}</span>
                    </button>
                  ))}
                </div>
              </div>
              <Button
                data-testid="settings-manage-plan"
                onClick={() => go("/billing")}
                className="w-full rounded-full text-white"
                style={{ backgroundColor: "#00E5FF" }}
              >
                {sub.tier === "free" ? "See plans & upgrade" : "Manage plan"}
              </Button>
              <div className="flex flex-col items-center gap-1.5 pt-1 text-[11px] text-slate-500" data-testid="settings-trust-footer">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#00E5FF]" strokeWidth={1.5} />
                  <span>Secure payments by Stripe · Cancel anytime</span>
                </div>
                <div className="flex items-center gap-2">
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[#00E5FF] transition-colors">Privacy</a>
                  <span className="text-slate-500">·</span>
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[#00E5FF] transition-colors">Terms</a>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      <section className="space-y-5">
        <div className="text-xs font-medium uppercase tracking-widest text-slate-400">Default preferences</div>
        <div className="space-y-3">
          <label className="text-sm text-slate-400">Default study mode</label>
          <div className="grid grid-cols-2 gap-2 p-1 bg-[#141B29] rounded-xl">
            {[["companion", "With virtual study companion"], ["alone", "Study alone"]].map(([id, label]) => (
              <button
                key={id}
                data-testid={`settings-mode-${id}`}
                onClick={() => { setStudyMode(id); savePrefs({ study_mode: id }); }}
                className={`py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  studyMode === id ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-500">Start new studies with the virtual study companion on, or study on your own by default.</p>
        </div>
        <div className="space-y-3">
          <label className="text-sm text-slate-400">Translation</label>
          <Select value={translation} onValueChange={(v) => { setTranslation(v); savePrefs({ translation: v }); }}>
            <SelectTrigger data-testid="settings-translation" className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0E131F]">
              {(config?.translations || []).map((t) => (
                <SelectItem key={t.id} value={t.id} disabled={!t.available}>
                  {t.short} — {t.name}{!t.available ? " (licensed key needed)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {user?.role === "admin" && (
        <section className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-400">More</div>
          <button
            data-testid="link-admin"
            onClick={() => go("/admin")}
            className="w-full flex items-center justify-between bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 hover:border-[#00E5FF]/30 transition-colors"
          >
            <span className="flex items-center gap-3 text-slate-300">
              <LayoutDashboard className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
              Admin dashboard
            </span>
            <span className="flex items-center gap-2">
              {unreplied > 0 && (
                <span data-testid="settings-unreplied-badge" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#00E5FF] text-[#07090E] text-[11px] font-semibold">
                  {unreplied}
                </span>
              )}
              <ChevronRight className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
            </span>
          </button>
        </section>
      )}

      <section className="space-y-3" data-testid="settings-updates">
        <button
          data-testid="updates-toggle"
          onClick={() => setUpdatesOpen((o) => !o)}
          className="w-full flex items-center justify-between bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 hover:border-[#00E5FF]/30 transition-colors"
        >
          <span className="flex items-center gap-3 text-slate-300">
            <Rocket className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
            <span className="text-left">
              <span className="block font-medium">What's new</span>
              {updates[0] && <span className="block text-xs text-slate-500">{updates[0].version} · {updates[0].title}</span>}
            </span>
          </span>
          {updatesOpen ? <ChevronUp className="w-5 h-5 text-slate-500" strokeWidth={1.5} /> : <ChevronDown className="w-5 h-5 text-slate-500" strokeWidth={1.5} />}
        </button>
        {updatesOpen && (
          <div className="rounded-2xl border border-[#1E273D] bg-[#0E131F] p-5 space-y-4 max-h-80 overflow-y-auto" data-testid="updates-panel">
            {updates.length === 0 ? (
              <p className="text-sm text-slate-500">No updates logged yet.</p>
            ) : (
              updates.map((u) => (
                <div key={u.id} data-testid={`update-${u.id}`} className="pb-3 border-b border-[#1E273D] last:border-0 last:pb-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#00E5FF] bg-[#10222A] rounded-full px-2 py-0.5">{u.version}</span>
                    <span className="text-sm font-medium text-slate-200">{u.title}</span>
                  </div>
                  {u.description && <p className="text-xs text-slate-400 mt-1 leading-relaxed">{u.description}</p>}
                  {u.created_at && <p className="text-[11px] text-slate-500 mt-1">{new Date(u.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>}
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {user?.has_password && (
        <section className="space-y-3" data-testid="settings-security">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-400">Security</div>
          <button
            data-testid="change-password-toggle"
            onClick={() => setPwOpen((o) => !o)}
            className="w-full flex items-center justify-between bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 hover:border-[#00E5FF]/30 transition-colors"
          >
            <span className="flex items-center gap-3 text-slate-300">
              <KeyRound className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
              <span className="font-medium">Change password</span>
            </span>
            {pwOpen ? <ChevronUp className="w-5 h-5 text-slate-500" strokeWidth={1.5} /> : <ChevronDown className="w-5 h-5 text-slate-500" strokeWidth={1.5} />}
          </button>
          {pwOpen && (
            <form onSubmit={changePassword} className="rounded-2xl border border-[#1E273D] bg-[#0E131F] p-5 space-y-3" data-testid="change-password-panel">
              <Input
                data-testid="current-password"
                type="password"
                placeholder="Current password"
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                required
                className="h-12 rounded-xl border-[#29344D]"
              />
              <Input
                data-testid="new-password"
                type="password"
                placeholder="New password (min 6 characters)"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                className="h-12 rounded-xl border-[#29344D]"
              />
              <Button
                data-testid="change-password-submit"
                type="submit"
                disabled={pwBusy || !curPw || !newPw}
                className="w-full rounded-full text-white"
                style={{ backgroundColor: "#00E5FF" }}
              >
                {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          )}
        </section>
      )}

      <section className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6">
        <p className="text-lg italic text-slate-300 leading-relaxed">
          "Scripture is the belief system. If anything doesn't line up with Scripture, it is not truth."
        </p>
        <p className="text-xs text-slate-400 mt-3 leading-relaxed">
          This virtual study companion holds everyone — including the founder — to the same standard: the Word. It is a
          study tool, not a replacement for a pastor, a church, or the Bible itself.
        </p>
      </section>

      <Button
        data-testid="logout-btn"
        onClick={doLogout}
        variant="outline"
        className="w-full h-12 rounded-full border-[#29344D] text-slate-400 hover:text-red-500 hover:border-red-500/30"
      >
        <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Sign out
      </Button>
    </div>
  );
}
